/**
 * **Abholung am Vorabend, Rückgabe am Morgen** — die Randtage, die nicht
 * berechnet werden (Migration 0286, 08.10.2026).
 *
 * Lisa (Testerin): *„Was ist, wenn der Kunde das Gerät schon am Abend zum
 * Feierabend holen will und wir lassen das zu? … Ich will den Tag nicht
 * berechnen, erst ab dem Morgen, und heute Abend abholen."* Luca hat
 * entschieden: eine **Regel des Betriebs**, automatisch wirksam, und das
 * Gleiche für die Rückgabe.
 *
 * Die Regel, in zwei Sätzen:
 *
 * - Liegt die **Übergabe** am Miettag vor dem **geplanten** Mietbeginn und zur
 *   oder nach der Vorabend-Uhrzeit, beginnt die Berechnung mit dem geplanten
 *   Mietbeginn.
 * - Liegt die **Rücknahme** am Miettag nach dem **geplanten** Mietende und zur
 *   oder vor der Morgen-Uhrzeit, endet die Berechnung mit dem geplanten
 *   Mietende.
 *
 * Alles andere bleibt, wie es ist (Regel 3: das Ist entscheidet). Wer vor der
 * Vorabend-Uhrzeit abholt oder nach der Morgen-Uhrzeit zurückbringt, bezahlt
 * den Tag.
 *
 * **„Arbeitstag" heißt hier: Miettag des Mietkalenders der Position** — im
 * Fünftagekalender ist der Vorabend eines Montags der Freitag, im
 * Siebentagekalender der Sonntag. Feiertage kennt der Kalender nicht (3.1).
 *
 * **Zählt der Anreisetag ohnehin nicht** (`ersterTagZaehlt = false`), greift
 * der Vorabend nicht: Der Abholtag ist dann schon der gestrichene erste Tag,
 * und beides zusammen nähme dem Kunden einen Tag, den er hatte. Dasselbe
 * spiegelbildlich für den Abreisetag und den Morgen.
 *
 * **Die Uhrzeit je Wochentag** (0288, 08.10.2026). Luca: *„Du musst pro Tag
 * den Grenzwert setzen können, dass die Abholung freitags bis 12 Uhr
 * erfolgt."* Wer am Freitag für Montag holt, kann nicht bis 16:00 warten.
 * Maßgeblich ist der **Wochentag des Abholtags** (des Vorabends, nicht des
 * Mietbeginns); fehlt er in `vorabend_je_tag`, gilt `vorabend_ab`. `null`
 * an einem Tag heißt: an diesem Tag gibt es keinen Vorabend. Ist
 * `vorabend_ab` leer, ist die Regel ganz aus — auch für die Abweichungen.
 *
 * **Tage ohne Abholung werden übersprungen** (0288, entschieden von Luca am
 * 08.10.2026 nach Lisas Interview, `docs/uebergabe/2026-10-08-interview-
 * mietkalender-kundentyp.md`). Steht ein Wochentag auf „Kein Abholtag"
 * (`null`), kann dort weder Vorabend noch Morgen liegen:
 *
 * - **Vorabend**: Die eingetragene Mietdauer entscheidet. 7-Tage-Woche,
 *   Mietbeginn Montag, Samstag und Sonntag ohne Abholung → der Vorabend ist
 *   der Freitag, und Freitag bis Sonntag werden nicht berechnet.
 * - **Morgen**: Frei ist nur der Rückgabetag selbst. Mietende Freitag,
 *   Rückgabe Montag früh → der Montag zählt nicht, das Wochenende dazwischen
 *   zählt, soweit der Kalender es als Miettag kennt (7-Tage-Woche ja,
 *   5-Tage-Woche nein). Nur der **erste** mögliche Morgen ist frei; wer
 *   später kommt, zahlt.
 *
 * Diese Datei rechnet nur. **Wann** sie gefragt wird — beim Übergeben und
 * Zurücknehmen, mit der Einstellung, die in diesem Augenblick gilt —, und dass
 * das Ergebnis an der Belegung festgeschrieben wird, steht in der API
 * (`dienste/ist.ts`, `randtageFestschreiben`); der Lauf liest es von dort.
 */
import { ausTagNummer, miettage, tagNummer, versatzBerlinMs, zaehltAlsMiettag } from "./kalender.js";
import { alsIso } from "./typen.js";
const MS_JE_MINUTE = 60_000;
const UHRZEIT = /^([01]\d|2[0-3]):([0-5]\d)(?::\d{2})?$/;
/** `'16:00'` → 960. Unlesbares → `null` (dann greift nichts). */
export function minutenAusUhrzeit(uhr) {
    if (!uhr)
        return null;
    const m = UHRZEIT.exec(uhr.trim());
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}
/** Die Minute des Tages in deutscher Zeit — 17:30 MESZ ist 1050. */
export function minuteImBetrieb(zeitpunkt) {
    const ms = zeitpunkt.getTime() + versatzBerlinMs(zeitpunkt);
    return Math.floor((((ms % 86_400_000) + 86_400_000) % 86_400_000) / MS_JE_MINUTE);
}
/** `HH:MM` aus einer Uhrzeit, wie die Datenbank sie liefert (`16:00:00` → `16:00`). */
function kurz(uhr) {
    return uhr.trim().slice(0, 5);
}
/** Der Wochentag einer Tagesnummer, `1` = Montag … `7` = Sonntag. */
export function wochentagIso(tagnummer) {
    const tag = ausTagNummer(tagnummer).getUTCDay();
    return tag === 0 ? 7 : tag;
}
/**
 * **Die Vorabend-Uhrzeit für diesen Abholtag** (`HH:MM`) — die Abweichung des
 * Wochentags, sonst die allgemeine. `null` heißt: an diesem Tag kein Vorabend
 * (oder die Regel ist aus).
 */
export function vorabendUhrFuer(tagnummer, regel) {
    if (minutenAusUhrzeit(regel.vorabend_ab) === null)
        return null;
    const je = regel.vorabend_je_tag;
    const schluessel = String(wochentagIso(tagnummer));
    const uhr = je && Object.prototype.hasOwnProperty.call(je, schluessel) ? je[schluessel] ?? null : regel.vorabend_ab;
    return minutenAusUhrzeit(uhr) === null ? null : kurz(uhr);
}
/**
 * Ist an diesem Wochentag Abholung bzw. Rückgabe? Nein nur, wenn der Tag in
 * `vorabend_je_tag` ausdrücklich auf `null` („Kein Abholtag") steht.
 */
function istAbholtag(tagnummer, regel) {
    const je = regel.vorabend_je_tag;
    const schluessel = String(wochentagIso(tagnummer));
    return !(je && Object.prototype.hasOwnProperty.call(je, schluessel) && je[schluessel] === null);
}
/** Ein Tag, an dem Vorabend oder Morgen liegen kann: Miettag des Kalenders und Abholtag. */
function randtagMoeglich(tagnummer, kalender, regel) {
    return zaehltAlsMiettag(tagnummer, kalender) && istAbholtag(tagnummer, regel);
}
/** Höchstens so weit wird gesucht — steht jeder Tag auf „Kein Abholtag", gibt es keinen. */
const SUCHE_TAGE = 14;
/** Der letzte mögliche Randtag vor diesem Tag — oder `null`. */
function randtagVorher(tagnummer, kalender, regel) {
    for (let n = tagnummer - 1; n >= tagnummer - SUCHE_TAGE; n -= 1) {
        if (randtagMoeglich(n, kalender, regel))
            return n;
    }
    return null;
}
/** Der erste mögliche Randtag nach diesem Tag — oder `null`. */
function randtagNachher(tagnummer, kalender, regel) {
    for (let n = tagnummer + 1; n <= tagnummer + SUCHE_TAGE; n += 1) {
        if (randtagMoeglich(n, kalender, regel))
            return n;
    }
    return null;
}
/** Der letzte Miettag vor diesem Tag — nach dem Kalender der Position. */
function miettagVorher(tagnummer, kalender) {
    let n = tagnummer - 1;
    while (!zaehltAlsMiettag(n, kalender))
        n -= 1;
    return n;
}
/**
 * **Die beiden Randtage einer Miete** — wann eine Abholung bzw. Rückgabe
 * nicht berechnet würde.
 *
 * `planVon` und `planBis` sind der **verkaufte** Zeitraum (die Position), als
 * Kalendertag (UTC-Mitternacht) oder Zeitpunkt; gelesen wird der deutsche Tag.
 * Fehlt ein Ende, gibt es keinen Morgen.
 *
 * Gebraucht von der API beim Festschreiben und vom Telefon für den Hinweis
 * „wird nicht berechnet" vor der Unterschrift — dieselbe Antwort an beiden
 * Stellen.
 */
export function randtage(planVon, planBis, kalender, regel) {
    const bis = minutenAusUhrzeit(regel.morgen_bis) === null ? null : kurz(regel.morgen_bis);
    let vorabend = null;
    let morgen = null;
    if (planVon && kalender.ersterTagZaehlt) {
        const beginn = tagNummer(planVon);
        const abholtag = randtagVorher(beginn, kalender, regel);
        const ab = abholtag === null ? null : vorabendUhrFuer(abholtag, regel);
        if (abholtag !== null && ab !== null) {
            vorabend = { tag: alsIso(ausTagNummer(abholtag)), ab, beginn: alsIso(ausTagNummer(beginn)) };
        }
    }
    if (planBis && bis !== null && kalender.letzterTagZaehlt) {
        const ende = tagNummer(planBis);
        const rueckgabetag = randtagNachher(ende, kalender, regel);
        if (rueckgabetag !== null) {
            // Berechnet wird bis zum letzten Miettag vor dem Rückgabetag: Was dazwischen
            // als Miettag zählt (das Wochenende einer 7-Tage-Woche), zahlt der Kunde.
            const bisTag = Math.max(ende, miettagVorher(rueckgabetag, kalender));
            morgen = { tag: alsIso(ausTagNummer(rueckgabetag)), bis, ende: alsIso(ausTagNummer(bisTag)) };
        }
    }
    return { vorabend, morgen };
}
/**
 * Greift der Vorabend für diese Übergabe? Dann der Tag, ab dem berechnet wird
 * (ISO), sonst `null`.
 */
export function vorabendGreift(uebergabe, vorabend) {
    if (!vorabend)
        return null;
    if (alsIso(uebergabe) !== vorabend.tag)
        return null;
    const ab = minutenAusUhrzeit(vorabend.ab);
    return ab !== null && minuteImBetrieb(uebergabe) >= ab ? vorabend.beginn : null;
}
/**
 * Greift der Morgen für diese Rücknahme? Dann der Tag, bis zu dem berechnet
 * wird (ISO), sonst `null`. Die Uhrzeit gilt einschließlich: 08:00 bei
 * „bis 08:00" zählt nicht.
 */
export function morgenGreift(ruecknahme, morgen) {
    if (!morgen)
        return null;
    if (alsIso(ruecknahme) !== morgen.tag)
        return null;
    const bis = minutenAusUhrzeit(morgen.bis);
    return bis !== null && minuteImBetrieb(ruecknahme) <= bis ? morgen.ende : null;
}
/**
 * **Das berechnete Fenster einer Miete** — das Ist, um die Randtage gekürzt,
 * die festgeschrieben sind.
 *
 * `berechnetVon`/`berechnetBis` kommen aus der Belegung (`billing_from`,
 * `billing_until`, 0286) und sind Kalendertage. Ohne sie gilt das Ist
 * unverändert. Endet das Ist vor dem berechneten Beginn — abgeholt am Abend,
 * noch am selben Abend zurück —, greift der Vorabend nicht: Der Kunde hatte
 * das Gerät nur an diesem einen Tag, und der zählt. Bei offenem Ende (`offen`)
 * gilt der berechnete Beginn immer; endet die Periode davor, liegt `von` hinter
 * `bis`, und die Periode trägt keinen Miettag.
 */
export function berechnetesFenster(istVon, istBis, berechnetVon, berechnetBis, 
/** Noch draußen (offenes Ende): `istBis` ist dann das Ende der Periode, keine Rücknahme. */
offen = false) {
    let von = istVon;
    let bis = istBis;
    let vorabend = false;
    let morgen = false;
    if (berechnetVon && tagNummer(berechnetVon) > tagNummer(istVon)
        && (offen || tagNummer(istBis) >= tagNummer(berechnetVon))) {
        von = berechnetVon;
        vorabend = true;
    }
    if (!offen && berechnetBis && tagNummer(berechnetBis) < tagNummer(istBis) && tagNummer(berechnetBis) >= tagNummer(von)) {
        bis = berechnetBis;
        morgen = true;
    }
    return { von, bis, vorabend, morgen };
}
/**
 * **Liegt zwischen der möglichen Abholung und dem Mietbeginn ein Wochenende?**
 * Ja, wenn vor dem Mietbeginn Tage auf „Kein Abholtag" stehen und der Tag
 * davor einen Vorabend hat — dann kann der Kunde schon dort holen, und
 * nichts dazwischen wird berechnet. Ohne „Kein Abholtag" (Vorgabe) nie.
 *
 * Bewusst **ohne** den Kalender des Geräts: Beim Vermieten steht oft noch
 * kein Gerät fest. Die Rechnung selbst (`randtage`) nimmt den Kalender dazu;
 * sie kommt bei Mietbeginn Montag und Sa/So ohne Abholung in jeder Woche auf
 * denselben Freitag.
 */
export function fruehabholung(planVon, regel) {
    if (!planVon || minutenAusUhrzeit(regel.vorabend_ab) === null)
        return null;
    const beginn = tagNummer(planVon);
    let n = beginn - 1;
    while (n >= beginn - SUCHE_TAGE && !istAbholtag(n, regel))
        n -= 1;
    const tageDazwischen = beginn - n - 1;
    if (tageDazwischen < 1 || n < beginn - SUCHE_TAGE)
        return null;
    const ab = vorabendUhrFuer(n, regel);
    if (ab === null)
        return null;
    return { tag: alsIso(ausTagNummer(n)), ab, beginn: alsIso(ausTagNummer(beginn)), tageDazwischen };
}
/**
 * **Wurde nach dem eingetragenen Mietende zurückgegeben?** Dann das Mietende,
 * der letzte berechnete Tag und die Miettage beider Zeiträume — nach dem
 * Kalender der Position. `von`/`bis` sind der berechnete Zeitraum (Ist, um
 * Vorabend und Morgen gekürzt). Wer am Morgen nach dem Mietende kam und nichts
 * dazwischen zahlt, ist nicht „später": dann `null`.
 */
export function spaeteRueckgabe(planVon, planBis, von, bis, kalender) {
    if (!planVon || !planBis || !von || !bis)
        return null;
    if (tagNummer(bis) <= tagNummer(planBis))
        return null;
    const geplant = miettage(planVon, planBis, kalender);
    const berechnet = miettage(von, bis, kalender);
    return { mietende: alsIso(ausTagNummer(tagNummer(planBis))), berechnetBis: alsIso(ausTagNummer(tagNummer(bis))), geplant, berechnet };
}
//# sourceMappingURL=randtage.js.map